// The admin's own token pair, parked in sessionStorage while they use "Login as
// Customer": AdminClients.handleImpersonate / ClientDetailDrawer park it, and
// ImpersonationBanner.handleReturn restores it. An /api/admin/* call made from
// the customer view must carry this PARKED token; the active (customer) session
// is never used or touched here.
//
// Kept out of api.ts, which uses Vite's import.meta and so can't load under the
// package's plain-Node jest config (see ContentTab.test.tsx); api.ts wraps this.
import type { ApiResponse } from '@siteforge/shared';

export const PARKED_ADMIN_ACCESS = 'sf_original_accessToken';
export const PARKED_ADMIN_REFRESH = 'sf_original_refreshToken';

function readParked(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}

export function hasParkedAdminToken(): boolean {
  return !!readParked(PARKED_ADMIN_ACCESS);
}

/** Role in the parked admin token while impersonating (null otherwise). Only for
 *  showing/hiding admin UI; the server enforces the real role gate. */
export function getImpersonatorRole(): string | null {
  const part = readParked(PARKED_ADMIN_ACCESS)?.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
    return typeof payload?.role === 'string' ? payload.role : null;
  } catch {
    return null;
  }
}

// Single-flight like api.ts's refreshOnce, but for the parked admin pair. The
// server rotates the refresh token on use, so the new pair is parked again;
// otherwise "Return to Admin" would restore a revoked refresh token.
let inflightRefresh: Promise<string | null> | null = null;

function refreshParkedAdminToken(apiBase: string): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  const rt = readParked(PARKED_ADMIN_REFRESH);
  if (!rt) return Promise.resolve(null);
  inflightRefresh = (async () => {
    try {
      const res = await fetch(`${apiBase}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) return null;
      const body = await res.json();
      const next = body?.success ? body.data : null;
      if (!next?.accessToken || !next?.refreshToken) return null;
      try {
        sessionStorage.setItem(PARKED_ADMIN_ACCESS, next.accessToken);
        sessionStorage.setItem(PARKED_ADMIN_REFRESH, next.refreshToken);
      } catch { /* storage unavailable; this call still uses the fresh token */ }
      return next.accessToken as string;
    } catch {
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

/**
 * Send a JSON request with the parked admin token: one refresh + retry on a 401,
 * a friendly message when the parked session is gone or the role is refused,
 * and never a raw parse error. Same ApiResponse shape as api.ts's request().
 */
export async function parkedAdminRequest<T = unknown>(
  apiBase: string,
  path: string,
  init: RequestInit,
  opts: { forbiddenError?: string } = {},
): Promise<ApiResponse<T>> {
  const send = (token: string) => fetch(`${apiBase}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers as Record<string, string> | undefined), Authorization: `Bearer ${token}` },
  });
  const parked = readParked(PARKED_ADMIN_ACCESS);
  if (!parked) {
    return { success: false, error: 'Your admin session has expired. Use "Return to Admin", sign in again, then retry.' };
  }
  let res = await send(parked);
  if (res.status === 401) {
    const fresh = await refreshParkedAdminToken(apiBase);
    if (!fresh) {
      return { success: false, error: 'Your admin session has expired. Use "Return to Admin", sign in again, then retry.' };
    }
    res = await send(fresh);
  }
  if (res.status === 403) {
    return { success: false, error: opts.forbiddenError || 'This needs a full admin account.' };
  }
  try {
    const data = await res.json();
    if (!res.ok && !data?.error) data.error = `Request failed with status ${res.status}`;
    return data;
  } catch {
    return { success: false, error: `Server returned ${res.status}: the response was incomplete or malformed. Please try again in a moment.` };
  }
}
